import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentResult({ mode }) {
  const [state, setState] = useState(mode === "cancel" ? "cancel" : "checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (mode !== "success") return;
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) { setState("error"); return; }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("success"); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setState("error"); return; }
      } catch {}
      if (attempts.current < 12) setTimeout(poll, 2500);
      else setState("timeout");
    };
    poll();
    return () => { stopped = true; };
  }, [mode]);

  const goHome = () => { window.location.href = "/"; };

  return (
    <div className="min-h-screen bg-[#18181b] flex items-center justify-center p-4" data-testid="payment-result-page">
      <div className="bg-white border border-zinc-200 p-8 max-w-sm w-full text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-10 h-10 text-orange-500 animate-spin mx-auto mb-3" />
            <h2 className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Confirming your payment…</h2>
            <p className="text-sm text-zinc-500 mt-1">This usually takes a few seconds.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }} data-testid="payment-success-title">You're subscribed!</h2>
            <p className="text-sm text-zinc-500 mt-1">Your account is unlocked on all devices. Thanks for supporting the app.</p>
            <Button onClick={goHome} className="mt-4 w-full bg-orange-600 hover:bg-orange-700 text-white rounded-none" data-testid="payment-continue-btn">Continue to App</Button>
          </>
        )}
        {state === "cancel" && (
          <>
            <XCircle className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Checkout canceled</h2>
            <p className="text-sm text-zinc-500 mt-1">No charge was made. You can subscribe anytime.</p>
            <Button onClick={goHome} className="mt-4 w-full bg-zinc-800 hover:bg-zinc-700 text-white rounded-none" data-testid="payment-back-btn">Back to App</Button>
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>
              {state === "timeout" ? "Still processing" : "Payment not completed"}
            </h2>
            <p className="text-sm text-zinc-500 mt-1">
              {state === "timeout" ? "Your payment may still be going through. Check back in a minute — if you were charged, your account will unlock automatically." : "Something went wrong or the payment was declined. No worries — you can try again."}
            </p>
            <Button onClick={goHome} className="mt-4 w-full bg-zinc-800 hover:bg-zinc-700 text-white rounded-none">Back to App</Button>
          </>
        )}
      </div>
    </div>
  );
}
